import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography, 
  Button, 
  Chip, 
  IconButton, 
  Tooltip,
  Stack,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  ToggleOn as ToggleIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../utils/auth';

type ApiKeyData = {
  id: number;
  name?: string;
  active: boolean;
  created_at?: string;
  last_used_at?: string;
  expires_at?: string;
  max_tokens_monthly: number;
  total_runs: number;
  total_tokens: number;
};

type CustomerData = {
  id: string;
  customer_tag: string;
  name: string;
};

export default function AdminCustomerApiKeys() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { user, permissions } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKeyData[]>([]);
  const [customer, setCustomer] = useState<CustomerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });

  // Check if user is admin on mount
  useEffect(() => {
    if (!isAdminUser(permissions?.userType)) {
      navigate('/dashboard');
    }
  }, [permissions, navigate]);

  const loadApiKeys = async () => {
    if (!customerId) return;
    
    setLoading(true);
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`/api/v2/admin/customers/${customerId}/api-keys`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      const data = await res.json();
      setApiKeys(data.data || []);
      setCustomer(data.customer || null);
    } catch (e: any) {
      setSnackbar({ 
        open: true, 
        message: e?.message || 'Failed to load API keys', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && isAdminUser(permissions?.userType)) {
      loadApiKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, user]);

  const handleViewRuns = (apiKeyId: number) => {
    navigate(`/dashboard/api-keys/${apiKeyId}/runs`);
  };

  const handleToggleStatus = async (apiKeyId: number) => {
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`/api/v2/admin/api-keys/${apiKeyId}/toggle-status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      const data = await res.json();
      setSnackbar({ 
        open: true, 
        message: data.message || 'API key status updated', 
        severity: 'success' 
      });
      
      // Reload the API keys list
      loadApiKeys();
    } catch (e: any) {
      setSnackbar({ 
        open: true, 
        message: e?.message || 'Failed to toggle API key status', 
        severity: 'error' 
      });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatTokens = (tokens: number) => {
    return tokens.toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Button 
            startIcon={<BackIcon />} 
            onClick={() => navigate('/dashboard/admin/customers')}
            sx={{ mb: 1 }}
          >
            Back to Customers
          </Button>
          <Typography variant="h5">
            API Keys for {customer?.name || 'Customer'}
          </Typography>
          {customer && (
            <Typography variant="body2" color="text.secondary">
              {customer.customer_tag} ({customer.id})
            </Typography>
          )}
        </Box>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Monthly Token Limit</TableCell>
              <TableCell align="right">Total Runs</TableCell>
              <TableCell align="right">Total Tokens</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiKeys.map(key => (
              <TableRow key={key.id} hover>
                <TableCell>{key.name || '-'}</TableCell>
                <TableCell>
                  <Chip 
                    label={key.active ? 'Active' : 'Inactive'} 
                    color={key.active ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {key.max_tokens_monthly > 0 ? formatTokens(key.max_tokens_monthly) : 'Unlimited'}
                </TableCell>
                <TableCell align="right">{formatTokens(key.total_runs)}</TableCell>
                <TableCell align="right">{formatTokens(key.total_tokens)}</TableCell>
                <TableCell>{formatDate(key.created_at)}</TableCell>
                <TableCell>{formatDate(key.last_used_at)}</TableCell>
                <TableCell>{formatDate(key.expires_at)}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="View Runs">
                      <IconButton 
                        size="small"
                        onClick={() => handleViewRuns(key.id)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={key.active ? "Deactivate" : "Activate"}>
                      <IconButton 
                        size="small"
                        onClick={() => handleToggleStatus(key.id)}
                        color={key.active ? "error" : "success"}
                      >
                        <ToggleIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {apiKeys.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {loading ? 'Loading…' : 'No API keys found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
